import { useEffect, useReducer, useRef, useState } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';
import type { PurchaseHistory } from '../modules/DataTransformSchema';
import { useProgress } from '../contexts/ProgressContext';
import { RETAILER_CONFIGS, ALL_STORES } from '../config/retailers';

import { DashboardPage } from './DashboardPage';
import { ErrorPage } from './ErrorPage';
import { LoadingPage } from './LoadingPage';
import { SignInPage } from './SignInPage';
import { SuccessPage } from './SuccessPage';
import { TapResearchNotEligiblePage } from './TapResearchNotEligiblePage';
import { PostConnectSurveyPage } from './PostConnectSurveyPage';
import { KrogerMembershipPage } from './KrogerMembershipPage';
import { GmailConnectPage } from './GmailConnectPage';
import { ViewDataButton } from '../lib/ui-kit/index.js';
import useAmazonDataConnection, {
  type DataConnectionResult,
} from '../hooks/useAmazonDataConnection';
import useSafewayDataConnection from '../hooks/useSafewayDataConnection';
import useWalmartDataConnection from '../hooks/useWalmartDataConnection';
import useDoorDashDataConnection from '../hooks/useDoorDashDataConnection';
import useTargetDataConnection from '../hooks/useTargetDataConnection';
import useBestbuyDataConnection from '../hooks/useBestbuyDataConnection';
import { apiClient } from '../api';
import { getSessionId } from '../lib/logger/client.js';
import { clientLogger } from '../utils/client-logger';
import { useInitialParamsSnapshot } from '../hooks/useInitialParams';
import { useFeatureFlag, useTenant } from '../FeatureFlags';
import { useRetailerOptions } from '../hooks/useRetailerOptions';
import { RetailerOptionList } from '../components/RetailerOptionList';
import { setPopstateIntercept } from '../utils/popstateGuard';
import { SURVEY_QUERY_PARAM_KROGER } from '../constants/surveyQueryParams';

type ConnectionState =
  | 'INITIAL'
  | 'CONNECTING'
  | 'SUCCESS'
  | 'POST_CONNECT_SURVEY'
  | 'CONNECTED_EMPTY'
  | 'CONNECTED_WITH_DATA'
  | 'ERROR';

type ConnectionAction =
  | { type: 'START_CONNECTION' }
  | { type: 'PROGRESS_STEP'; step: number }
  | { type: 'AUTH_COMPLETE' }
  | {
      type: 'CONNECTION_SUCCESS';
      data: PurchaseHistory[];
      isLoadingMore: boolean;
    }
  | { type: 'ADD_ITEMS'; items: PurchaseHistory[] }
  | { type: 'SET_ORDERS'; data: PurchaseHistory[]; isLoadingMore: boolean }
  | {
      type: 'CONNECTION_ERROR';
      error: string;
      code?: string;
      retailerName?: string;
    }
  | { type: 'RETRY_CONNECTION' }
  | { type: 'RESET_TO_INITIAL' }
  | { type: 'SHOW_DATA' }
  | { type: 'SHOW_POST_CONNECT_SURVEY' }
  | { type: 'SHOW_SUCCESS' }
  | { type: 'COMPLETE_POST_CONNECT_SURVEY' }
  | { type: 'SET_SESSION_ID'; sessionId: string }
  | { type: 'SYNC_PARTIAL_ERROR'; error: string };

type ConnectionStateData = {
  state: ConnectionState;
  orders: PurchaseHistory[];
  currentLoadingStep: number;
  isLoadingMore: boolean;
  sessionId: string | null;
  syncError: string | null;
  postConnectSurveyComplete: boolean;
  error: {
    title?: string;
    message?: string;
    details?: string;
    code?: string;
  } | null;
};

const getInitialConnectionState = (): ConnectionState => {
  if (window.location.pathname === '/connect/survey')
    return 'POST_CONNECT_SURVEY';
  return 'INITIAL';
};

const connectionReducer = (
  state: ConnectionStateData,
  action: ConnectionAction
): ConnectionStateData => {
  switch (action.type) {
    case 'START_CONNECTION':
      return {
        ...state,
        state: 'CONNECTING',
        currentLoadingStep: 1,
        error: null,
      };

    case 'PROGRESS_STEP':
      return {
        ...state,
        currentLoadingStep: action.step,
      };

    case 'AUTH_COMPLETE':
      return {
        ...state,
        state: 'CONNECTING',
        currentLoadingStep: 2,
      };

    case 'CONNECTION_SUCCESS': {
      return {
        ...state,
        state: 'SUCCESS',
        orders: action.data,
        currentLoadingStep: 4,
        isLoadingMore: action.isLoadingMore,
        error: null,
        syncError: null,
      };
    }

    case 'SHOW_DATA': {
      const hasOrders = state.orders.length > 0;
      return {
        ...state,
        state:
          hasOrders || state.isLoadingMore
            ? 'CONNECTED_WITH_DATA'
            : 'CONNECTED_EMPTY',
      };
    }

    case 'SHOW_POST_CONNECT_SURVEY':
      return {
        ...state,
        state: 'POST_CONNECT_SURVEY',
      };

    case 'SHOW_SUCCESS':
      return {
        ...state,
        state: 'SUCCESS',
      };

    case 'COMPLETE_POST_CONNECT_SURVEY':
      return {
        ...state,
        state: 'SUCCESS',
        postConnectSurveyComplete: true,
      };

    case 'ADD_ITEMS': {
      const combinedOrders = [...state.orders, ...action.items];
      const hasOrders = combinedOrders.length > 0;
      return {
        ...state,
        state: hasOrders ? 'CONNECTED_WITH_DATA' : 'CONNECTED_EMPTY',
        orders: combinedOrders,
        isLoadingMore: true,
        error: null,
      };
    }

    // Refresh orders from a still-running stream without navigating. Used once
    // we've advanced past the loading screen (SUCCESS / survey) so the
    // fire-and-forget purchase stream can't yank the user off their screen.
    case 'SET_ORDERS': {
      const hasOrders = action.data.length > 0;
      const onDashboard =
        state.state === 'CONNECTED_WITH_DATA' ||
        state.state === 'CONNECTED_EMPTY';

      return {
        ...state,
        state: onDashboard
          ? hasOrders || action.isLoadingMore
            ? 'CONNECTED_WITH_DATA'
            : 'CONNECTED_EMPTY'
          : state.state,
        orders: action.data,
        isLoadingMore: action.isLoadingMore,
      };
    }

    case 'CONNECTION_ERROR':
      return {
        ...state,
        state: 'ERROR',
        error: {
          title: 'Connection Failed',
          message: `Failed to connect to ${action.retailerName ?? 'Amazon'}. Please try again or contact support if the problem persists.`,
          details: action.error,
          code: action.code,
        },
      };

    case 'RETRY_CONNECTION':
      return {
        ...state,
        state: 'INITIAL',
        error: null,
        orders: [],
        currentLoadingStep: 0,
        syncError: null,
        postConnectSurveyComplete: false,
      };

    case 'RESET_TO_INITIAL':
      return {
        state: 'INITIAL',
        orders: [],
        currentLoadingStep: 0,
        isLoadingMore: false,
        error: null,
        sessionId: null,
        syncError: null,
        postConnectSurveyComplete: false,
      };

    case 'SET_SESSION_ID':
      return {
        ...state,
        sessionId: action.sessionId,
      };

    case 'SYNC_PARTIAL_ERROR':
      return {
        ...state,
        syncError: action.error,
      };

    default:
      return state;
  }
};

export function MainPage() {
  const { track, page } = useAnalytics();
  const { setOverride } = useProgress();
  const isPostConnectSurveyEnabled = useFeatureFlag('post_connect_survey');
  const tenant = useTenant();
  const retailerOptions = useRetailerOptions();
  const pageTracked = useRef(false);
  const { region, retailer, survey, tid } = useInitialParamsSnapshot();
  const retailerConfig =
    (retailer ? RETAILER_CONFIGS[retailer] : null) ??
    RETAILER_CONFIGS['amazon'];
  const storeConfig =
    ALL_STORES.find(
      (s) =>
        s.retailer_id === retailerConfig.retailer_id &&
        (s.region == null || s.region === region)
    ) || ALL_STORES[0]!;
  const [connectionState, dispatch] = useReducer(connectionReducer, {
    state: getInitialConnectionState(),
    orders: [],
    currentLoadingStep: 0,
    isLoadingMore: false,
    error: null,
    sessionId: null,
    syncError: null,
    postConnectSurveyComplete: false,
  });
  const [panelEligibility, setPanelEligibility] = useState<{
    eligible: boolean;
    redirectUrl: string | null;
  } | null>(null);

  // The landing state is read here so the tracked event carries it, while the
  // ref guard keeps later state changes from firing the event again.
  useEffect(() => {
    if (!pageTracked.current) {
      page('main', { initialState: connectionState.state });
      pageTracked.current = true;
    }
  }, [page, connectionState.state]);

  const handleConnectStart = () => {
    dispatch({ type: 'START_CONNECTION' });
    track('Connection Started');
  };

  const progressToStep = (step: number) => {
    dispatch({ type: 'PROGRESS_STEP', step });
  };

  const handleAuthComplete = (panelEligibility?: {
    eligible: boolean;
    redirectUrl: string | null;
  }) => {
    if (panelEligibility) {
      setPanelEligibility(panelEligibility);
    }
    dispatch({ type: 'SHOW_SUCCESS' });
    track('Authentication Completed', { step: 2 });

    void apiClient.updateProxyIpAddress().catch((error) => {
      clientLogger.info('updateProxyIpAddress failed on AUTH_COMPLETE', {
        error,
      });
    });

    const sessionId = getSessionId();
    if (sessionId) {
      dispatch({ type: 'SET_SESSION_ID', sessionId });
    }
  };

  const handleSuccessConnect = (
    data: PurchaseHistory[],
    isLoadingMore?: boolean
  ) => {
    // Already on dashboard — stream still running; update data without resetting to SuccessPage
    if (
      connectionState.state === 'CONNECTED_WITH_DATA' ||
      connectionState.state === 'CONNECTED_EMPTY'
    ) {
      dispatch({
        type: 'SET_ORDERS',
        data,
        isLoadingMore: isLoadingMore ?? false,
      });
      return;
    }

    // Already advanced past the loading screen (auth now jumps straight to
    // SUCCESS, and the user may be in the post-connect survey). The purchase
    // stream is fire-and-forget here: keep orders fresh for "View data" but
    // never navigate, or we'd kick the user out of the survey back to step 1.
    if (
      connectionState.state === 'SUCCESS' ||
      connectionState.state === 'POST_CONNECT_SURVEY'
    ) {
      dispatch({
        type: 'SET_ORDERS',
        data,
        isLoadingMore: isLoadingMore ?? false,
      });
      if (!isLoadingMore) {
        track('Connection Successful', {
          orderCount: data.length,
          hasOrders: data.length > 0,
          isCompleted: true,
        });
      }
      return;
    }

    dispatch({
      type: 'CONNECTION_SUCCESS',
      data,
      isLoadingMore: isLoadingMore ?? false,
    });

    if (!isLoadingMore) {
      track('Connection Successful', {
        orderCount: data.length,
        hasOrders: data.length > 0,
        isCompleted: !isLoadingMore,
      });
    }
  };

  const handlePartialSyncError = (error: string) => {
    dispatch({ type: 'SYNC_PARTIAL_ERROR', error });
  };

  const handleConnectionError = (errorDetails: string, code?: string) => {
    clientLogger.info('Connection failed:', { errorDetails });
    if (code === 'CAPACITY_FULL') {
      window.location.replace('/connect/waiting');
      track('Connection Capacity Full', { error: errorDetails });
      return;
    }

    dispatch({
      type: 'CONNECTION_ERROR',
      error: errorDetails,
      code,
      retailerName: storeConfig.retailer_name,
    });
    track('Connection Failed', { error: errorDetails });
  };

  const handleRetry = async () => {
    track('Connection Retried');
    await apiClient.releaseSyncCapacity();
    window.location.reload();
  };

  const handleGoHome = () => {
    dispatch({ type: 'RESET_TO_INITIAL' });
  };

  const handleContinueToSurvey = () => {
    if (!shouldShowPostConnectSurvey) return;

    dispatch({ type: 'SHOW_POST_CONNECT_SURVEY' });
    track('Post Connect Survey Started');
  };

  const amazonConnection = useAmazonDataConnection({
    store: storeConfig,
    onSuccessConnect: handleSuccessConnect,
    onConnectStart: handleConnectStart,
    onConnectionError: handleConnectionError,
    onPartialSyncError: handlePartialSyncError,
    onProgressStep: progressToStep,
    onAuthComplete: handleAuthComplete,
    onRetryConnection: handleRetry,
    onGoToInitial: handleGoHome,
  });

  const safewayConnection = useSafewayDataConnection({
    onSuccessConnect: handleSuccessConnect,
    onConnectStart: handleConnectStart,
    onConnectionError: handleConnectionError,
    onAuthComplete: handleAuthComplete,
    onGoToInitial: handleGoHome,
  });

  const walmartConnection = useWalmartDataConnection({
    onSuccessConnect: handleSuccessConnect,
    onConnectStart: handleConnectStart,
    onConnectionError: handleConnectionError,
    onPartialSyncError: handlePartialSyncError,
    onProgressStep: progressToStep,
    onAuthComplete: handleAuthComplete,
    onGoToInitial: handleGoHome,
  });

  const doorDashConnection = useDoorDashDataConnection({
    onSuccessConnect: handleSuccessConnect,
    onConnectStart: handleConnectStart,
    onConnectionError: handleConnectionError,
    onPartialSyncError: handlePartialSyncError,
    onProgressStep: progressToStep,
    onAuthComplete: handleAuthComplete,
    onGoToInitial: handleGoHome,
  });

  const targetConnection = useTargetDataConnection({
    onSuccessConnect: handleSuccessConnect,
    onConnectStart: handleConnectStart,
    onConnectionError: handleConnectionError,
    onProgressStep: progressToStep,
    onAuthComplete: handleAuthComplete,
    onGoToInitial: handleGoHome,
  });

  const bestbuyConnection = useBestbuyDataConnection({
    onSuccessConnect: handleSuccessConnect,
    onConnectStart: handleConnectStart,
    onConnectionError: handleConnectionError,
    onProgressStep: progressToStep,
    onAuthComplete: handleAuthComplete,
    onGoToInitial: handleGoHome,
  });

  const connectionByRetailer: Record<string, DataConnectionResult> = {
    amazon: amazonConnection,
    safeway: safewayConnection,
    walmart: walmartConnection,
    doordash: doorDashConnection,
    target: targetConnection,
    bestbuy: bestbuyConnection,
  };

  const activeConnection =
    connectionByRetailer[retailer ?? ''] ?? connectionByRetailer['amazon'];

  const {
    getSigninIdAndPollSigninStatus,
    signinId,
    setSelectedRegion,
    handleSubmitPreemptiveForm,
    handleSigninDone,
    isFollowUpForm,
    questionProTaskStatus,
    hasPrefetchedCredentials,
    shouldRedirectToExternal,
  } = activeConnection;

  // Sync region from URL param on mount
  useEffect(() => {
    setSelectedRegion(region);
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  const isExternalCompletionUser = shouldRedirectToExternal;
  const surveyId = survey?.trim() || null;
  const shouldShowPostConnectSurvey =
    surveyId !== null && isPostConnectSurveyEnabled;
  const showSurveyPrompt =
    shouldShowPostConnectSurvey && !connectionState.postConnectSurveyComplete;

  useEffect(() => {
    if (
      connectionState.state === 'POST_CONNECT_SURVEY' &&
      !shouldShowPostConnectSurvey
    ) {
      dispatch({ type: 'SHOW_SUCCESS' });
    }
  }, [connectionState.state, shouldShowPostConnectSurvey]);

  useEffect(() => {
    setPopstateIntercept(
      isFollowUpForm || connectionState.state === 'CONNECTING'
    );
  }, [isFollowUpForm, connectionState.state]);

  // Sync URL bar without triggering React Router remount
  useEffect(() => {
    // Gmail owns its own URL (see GmailConnectPage); connectionState never
    // advances for that strategy, so this effect must not fight it. The strategy
    // is stable per mount, so depending on it adds no extra runs.
    if (storeConfig.connection_strategy === 'gmail') return;
    if (connectionState.state === 'CONNECTING') {
      window.history.replaceState(null, '', '/connect/loading');
    } else if (connectionState.state === 'SUCCESS') {
      window.history.replaceState(null, '', '/connect/success');
    } else if (connectionState.state === 'POST_CONNECT_SURVEY') {
      window.history.replaceState(null, '', '/connect/survey');
    } else if (
      connectionState.state === 'CONNECTED_WITH_DATA' ||
      connectionState.state === 'CONNECTED_EMPTY'
    ) {
      window.history.replaceState(null, '', '/dashboard');
    } else if (connectionState.state === 'INITIAL') {
      window.history.replaceState(null, '', '/connect/signin');
    }
  }, [connectionState.state, storeConfig.connection_strategy]);

  // Push progress bar to 100% for steps 3–4 (loading, success, connected)
  useEffect(() => {
    const isLate =
      connectionState.state === 'CONNECTING' ||
      connectionState.state === 'SUCCESS' ||
      connectionState.state === 'POST_CONNECT_SURVEY' ||
      connectionState.state === 'CONNECTED_WITH_DATA' ||
      connectionState.state === 'CONNECTED_EMPTY';
    setOverride(isLate ? 100 : null);
  }, [connectionState.state, setOverride]);

  if (storeConfig.connection_strategy === 'gmail') {
    return (
      <GmailConnectPage
        onSuccess={() => track('Connection Successful', { brand: 'gmail' })}
      />
    );
  }

  // Render based on current state
  switch (connectionState.state) {
    case 'ERROR':
      return <ErrorPage error={connectionState.error!} onRetry={handleRetry} />;

    case 'CONNECTING':
      return (
        <LoadingPage
          autoComplete={false}
          initialStep={connectionState.currentLoadingStep}
          totalSteps={3}
          retailerName={storeConfig.retailer_name}
        />
      );

    case 'SUCCESS':
      if (tid && panelEligibility && !panelEligibility.eligible) {
        return (
          <TapResearchNotEligiblePage
            redirectUrl={panelEligibility.redirectUrl}
          />
        );
      }
      return (
        <>
          <SuccessPage
            isExternalCompletionUser={isExternalCompletionUser}
            tapResearchRedirectUrl={
              tid ? panelEligibility?.redirectUrl : undefined
            }
            showPostConnectSurvey={showSurveyPrompt}
            surveyId={surveyId}
            sessionId={connectionState.sessionId}
            retailerName={retailerConfig.retailer_name}
            onContinue={handleContinueToSurvey}
          />
          {tenant.flow.viewData &&
            !isExternalCompletionUser &&
            !showSurveyPrompt && (
              <ViewDataButton onClick={() => dispatch({ type: 'SHOW_DATA' })} />
            )}
        </>
      );

    case 'POST_CONNECT_SURVEY':
      if (!shouldShowPostConnectSurvey) return null;

      if (survey === SURVEY_QUERY_PARAM_KROGER) {
        return (
          <KrogerMembershipPage
            onBack={() => dispatch({ type: 'SHOW_SUCCESS' })}
            onSubmit={async ({ membershipNumber, photo }) => {
              const { redirectUrl } = await apiClient.uploadKrogerMembership(
                membershipNumber,
                photo
              );
              track('Kroger Membership Uploaded');
              if (redirectUrl) {
                window.location.href = redirectUrl;
                return;
              }
              dispatch({ type: 'COMPLETE_POST_CONNECT_SURVEY' });
            }}
          />
        );
      }

      return (
        <PostConnectSurveyPage
          surveyId={surveyId!}
          onBack={() => dispatch({ type: 'SHOW_SUCCESS' })}
        />
      );

    case 'INITIAL':
      if (!isFollowUpForm && hasPrefetchedCredentials) {
        return (
          <div
            className="flex-1 flex items-center justify-center py-12 px-6"
            role="status"
            aria-label="Preparing connection"
          >
            <div className="h-8 w-8 rounded-full border-2 border-rule border-t-primary animate-spin" />
          </div>
        );
      }
      return (
        <>
          <div
            className="flex-1 flex flex-col items-center justify-center py-12 px-6 pointer-events-none"
            aria-hidden="true"
          >
            <div className="w-full max-w-[480px]">
              <h2 className="font-display text-[clamp(22px,3vw,28px)] font-bold text-ink tracking-[-0.02em] leading-[1.2] mb-2">
                {tenant.copy.regionTitle}
              </h2>
              <p className="text-[15px] text-ink-muted mb-8">
                {tenant.copy.regionSubtitle}
              </p>
              <RetailerOptionList
                options={retailerOptions}
                selectedKey={storeConfig.retailer_key}
              />
            </div>
          </div>

          <SignInPage
            retailerName={retailerConfig.retailer_name}
            onStartConnection={getSigninIdAndPollSigninStatus}
            signinId={signinId}
            onSubmitPreemptiveForm={handleSubmitPreemptiveForm}
            showFollowUpForm={isFollowUpForm}
            questionProTaskStatus={questionProTaskStatus}
            requiresPassword={retailerConfig.requires_password}
            showPasswordHelp={retailerConfig.retailer_id === 'amazon'}
            retailerDomain={storeConfig.domain}
            emailInputType={retailerConfig.login_input_type ?? 'email'}
            emailPlaceholder={retailerConfig.login_placeholder ?? 'Email'}
            onCloseFollowUpForm={async () => {
              await apiClient.releaseSyncCapacity();
              window.location.href = '/connect';
            }}
            onSigninDone={handleSigninDone}
          />
        </>
      );

    case 'CONNECTED_EMPTY':
      return (
        <DashboardPage
          orders={connectionState.orders}
          onRetryConnection={handleRetry}
          onConnectAnother={handleGoHome}
          isEmpty={true}
          sessionId={connectionState.sessionId}
          isQuestionProTask={isExternalCompletionUser}
          startConfirmed={true}
          retailerName={retailerConfig.retailer_name}
          syncError={connectionState.syncError}
        />
      );

    case 'CONNECTED_WITH_DATA':
      return (
        <DashboardPage
          orders={connectionState.orders}
          onRetryConnection={handleRetry}
          onConnectAnother={handleGoHome}
          isEmpty={false}
          isLoadingMore={connectionState.isLoadingMore}
          sessionId={connectionState.sessionId}
          isQuestionProTask={isExternalCompletionUser}
          startConfirmed={true}
          retailerName={retailerConfig.retailer_name}
          syncError={connectionState.syncError}
        />
      );

    default:
      return null;
  }
}
